import { useEffect, useMemo, useState } from "react";
import { Loader2, Upload, ImageIcon, Pencil, X, Search, Check } from "lucide-react";
import { toast } from "sonner";
import AdminShell from "@/components/admin/AdminShell";
import {
  listCollection,
  updateCollectionItem,
  listMedia,
  uploadMedia,
} from "@/lib/adminCmsApi";

/**
 * Site Images admin — every named image slot on the website in one place.
 * Each row shows the current image + a "Change image" button that opens
 * a modal where the admin can either pick from the Media Library or
 * upload a new file directly to Cloudinary.
 */
export default function AdminSiteImages() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pickerOpenFor, setPickerOpenFor] = useState(null); // slot object

  const load = async () => {
    setLoading(true);
    try {
      const rows = await listCollection("site-images");
      setSlots(rows.sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch (e) {
      toast.error("Failed to load site images");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onPick = async (url, altText = "") => {
    if (!pickerOpenFor) return;
    try {
      await updateCollectionItem("site-images", pickerOpenFor.id, {
        slot_key: pickerOpenFor.slot_key,
        label: pickerOpenFor.label,
        location: pickerOpenFor.location || "",
        url,
        alt_text: altText || pickerOpenFor.alt_text || "",
        order: pickerOpenFor.order || 0,
        is_visible: true,
      });
      toast.success(`Image updated for "${pickerOpenFor.label}"`);
      setPickerOpenFor(null);
      load();
    } catch (e) {
      toast.error("Update failed");
    }
  };

  return (
    <AdminShell title="Site Images">
      <p className="mb-6 text-sm text-ags-text-muted max-w-2xl">
        Every image displayed on the public site is bound to a named slot below.
        Click <em>Change image</em> on any card to pick from the Media Library
        or upload a new file — the change is live within seconds.
      </p>

      {loading ? (
        <div className="p-16 flex items-center justify-center">
          <Loader2 className="animate-spin" />
        </div>
      ) : (
        <div
          data-testid="site-images-grid"
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        >
          {slots.map((s) => (
            <div
              key={s.id}
              data-testid={`site-image-slot-${s.slot_key}`}
              className="bg-white border border-ags-hairline flex flex-col"
            >
              <div className="aspect-[16/10] bg-ags-cream flex items-center justify-center overflow-hidden">
                {s.url ? (
                  <img
                    src={s.url}
                    alt={s.alt_text || s.label}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon size={32} className="text-[#0E2A57]/30" />
                )}
              </div>
              <div className="p-3 flex-1 flex flex-col">
                <div className="font-heading font-bold text-sm text-[#0E2A57] leading-tight">
                  {s.label}
                </div>
                <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-ags-text-muted">
                  {s.location}
                </div>
                <div className="mt-1 font-mono text-[9px] text-[#94A3B8] break-all">
                  slot: <code>{s.slot_key}</code>
                </div>
                <button
                  onClick={() => setPickerOpenFor(s)}
                  data-testid={`change-image-${s.slot_key}`}
                  className="mt-3 inline-flex items-center justify-center gap-2 bg-[#0E2A57] hover:bg-[#050B14] text-white font-mono text-[10px] uppercase tracking-[0.22em] py-2"
                >
                  <Pencil size={12} /> Change image
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pickerOpenFor && (
        <MediaPickerModal
          slot={pickerOpenFor}
          onClose={() => setPickerOpenFor(null)}
          onPick={onPick}
        />
      )}
    </AdminShell>
  );
}

// ---------- Media Picker Modal ----------
function MediaPickerModal({ slot, onClose, onPick }) {
  const [tab, setTab] = useState("library"); // library | upload
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [altText, setAltText] = useState(slot.alt_text || "");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const rows = await listMedia({
          resource_type: "image",
          q: q || undefined,
        });
        setItems(rows);
      } catch (e) {
        toast.error("Media library load failed");
      } finally {
        setLoading(false);
      }
    })();
  }, [q]);

  const filtered = useMemo(() => items || [], [items]);

  const onUploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const media = await uploadMedia(file, {
        alt_text: altText,
        folder: "ags/site-images",
      });
      toast.success("Uploaded");
      await onPick(media.url, altText || media.alt_text || "");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="border-b border-ags-hairline p-4 flex items-center justify-between">
          <div>
            <div className="font-heading font-bold text-lg text-[#0E2A57]">
              Change image · {slot.label}
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-ags-text-muted">
              {slot.location} · slot: {slot.slot_key}
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-ags-cream"
            data-testid="picker-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-ags-hairline px-4 flex gap-1">
          <button
            onClick={() => setTab("library")}
            data-testid="picker-tab-library"
            className={`px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] ${
              tab === "library"
                ? "text-[#0E2A57] border-b-2 border-[#2BB6E5]"
                : "text-ags-text-muted hover:text-[#0E2A57]"
            }`}
          >
            Media Library
          </button>
          <button
            onClick={() => setTab("upload")}
            data-testid="picker-tab-upload"
            className={`px-4 py-3 font-mono text-[10px] uppercase tracking-[0.22em] ${
              tab === "upload"
                ? "text-[#0E2A57] border-b-2 border-[#2BB6E5]"
                : "text-ags-text-muted hover:text-[#0E2A57]"
            }`}
          >
            Upload New
          </button>
        </div>

        {/* Alt text field */}
        <div className="px-4 py-3 border-b border-ags-hairline">
          <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-ags-text-muted mb-1">
            Alt Text (optional)
          </label>
          <input
            type="text"
            value={altText}
            onChange={(e) => setAltText(e.target.value)}
            placeholder="Descriptive alt text for accessibility"
            className="w-full border border-ags-hairline px-3 py-2 text-sm focus:border-[#0E2A57] focus:outline-none"
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto p-4">
          {tab === "library" ? (
            <>
              <div className="mb-4 relative">
                <Search
                  size={14}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-ags-text-muted"
                />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search library…"
                  className="pl-9 pr-3 py-2.5 w-full border border-ags-hairline focus:border-[#0E2A57] focus:outline-none"
                />
              </div>
              {loading ? (
                <div className="p-12 flex justify-center">
                  <Loader2 className="animate-spin" />
                </div>
              ) : filtered.length === 0 ? (
                <div className="p-12 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-ags-text-muted">
                  No images in the library yet — try Upload New.
                </div>
              ) : (
                <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {filtered.map((m) => (
                    <button
                      key={m.id}
                      data-testid={`pick-media-${m.id}`}
                      onClick={() => onPick(m.url, altText || m.alt_text || "")}
                      className="group relative bg-ags-cream aspect-square overflow-hidden border-2 border-transparent hover:border-[#2BB6E5]"
                    >
                      <img
                        src={m.url}
                        alt={m.alt_text || m.original_filename}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-[#0E2A57]/70 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <div className="bg-white text-[#0E2A57] px-3 py-1.5 font-mono text-[9px] uppercase tracking-[0.22em] flex items-center gap-1.5">
                          <Check size={11} /> Pick
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="max-w-md mx-auto py-8">
              <div className="border-2 border-dashed border-[#0E2A57]/20 p-8 text-center">
                <Upload size={28} className="mx-auto text-[#0E2A57]/40 mb-3" />
                <p className="text-sm text-ags-text-muted mb-4">
                  Choose an image file to upload. It will be pushed to
                  Cloudinary, added to the library, and assigned to this slot
                  automatically.
                </p>
                <label className="inline-flex items-center gap-2 bg-[#0E2A57] hover:bg-[#050B14] text-white cursor-pointer px-5 py-3 font-mono text-[10px] uppercase tracking-[0.22em]">
                  {uploading ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Uploading…
                    </>
                  ) : (
                    <>
                      <Upload size={14} /> Select image
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    disabled={uploading}
                    className="hidden"
                    onChange={(e) => onUploadFile(e.target.files?.[0])}
                    data-testid="picker-upload-input"
                  />
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
